import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { styles } from './CarrierWidget.styles';
import { useGetTopCarriersMutation } from 'src/features/carrier/carrierEngineApi';
import { TouchableOpacity, TouchableWithoutFeedback, View, LayoutAnimation } from 'react-native';
import Typography from 'src/components/Text/Text';

// Screens where the CarrierWidget should not be displayed
// const excludedScreens: string[] = [];

const CarrierWidget: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [getTopCarriers, { data, isLoading, error }] = useGetTopCarriersMutation();

  // const currentRouteName = useNavigationState((state) => state.routes[state.index]?.name);

  const currentCompanyId = useSelector((state: RootState) => state.company.company?.id);
  const vehicles = useSelector((state: RootState) => state.vehicles.vehicles);
  const drivers = useSelector((state: RootState) => state.drivers.drivers);
  const freights = useSelector((state: RootState) => state.freights.companyCommodities);
  const application = useSelector((state: RootState) => state.application.application);
  const loss = useSelector((state: RootState) => state.loss);

  const toggleExpand = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setIsExpanded((prev) => !prev);
  };

  const handleClose = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setIsExpanded(false);
    setShowLoading(false);
  };

  // Delay showing loading state to prevent flicker
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (isLoading) {
      timeoutId = setTimeout(() => setShowLoading(true), 200);
    } else {
      setShowLoading(false);
    }
    return () => clearTimeout(timeoutId);
  }, [isLoading]);

  useEffect(() => {
    if (isExpanded && currentCompanyId) {
      getTopCarriers({
        companyId: currentCompanyId,
      });
    }
  }, [
    isExpanded,
    currentCompanyId,
    vehicles,
    drivers,
    freights,
    application,
    loss,
    getTopCarriers,
  ]);

  // Don't render widget on excluded screens
  // if (excludedScreens.includes(currentRouteName)) {
  //   return null;
  // }

  return (
    <>
      {!isExpanded ? (
        // Floating Button - Only show when not expanded
        <TouchableOpacity style={styles.floatingButton} onPress={toggleExpand}>
          <Typography variant="h4" style={styles.buttonText}>
            Carriers
          </Typography>
        </TouchableOpacity>
      ) : (
        // Expanded Panel - Only show when expanded
        <TouchableWithoutFeedback onPress={handleClose}>
          <View style={styles.overlay}>
            <TouchableWithoutFeedback>
              <View style={styles.expandedContainer}>
                {showLoading && (
                  <Typography variant="body2" style={styles.placeholderText}>
                    Loading...
                  </Typography>
                )}
                {error && (
                  <Typography variant="body2" style={styles.placeholderText}>
                    Failed to load carriers.
                  </Typography>
                )}
                {!showLoading && !error && data && data.length > 0 && (
                  <View>
                    {data
                      .filter((carrier) => carrier.carrierId && carrier.carrierName)
                      .map((carrier) => (
                        <View key={carrier.carrierId} style={styles.carrierListItem}>
                          <Typography variant="title1" style={styles.carrierText}>
                            {carrier.label} - {carrier.carrierName} | {carrier.rankLabel}
                          </Typography>
                        </View>
                      ))}
                  </View>
                )}
                {!showLoading && !error && (!data || data.length === 0) && (
                  <Typography variant="body2" style={styles.placeholderText}>
                    No carriers available.
                  </Typography>
                )}
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      )}
    </>
  );
};

export default CarrierWidget;
